/**
 * Alerts tracking events for the subscriptions (wizard) variant.
 *
 * Naming: `alerts.<action>` — all snake_case, matching the alerts tracking
 * convention so both creation flows (compact + wizard) share the same
 * event namespace and can be segmented by `variant`.
 *
 * `variant` is always `"wizard"` for the subscriptions flow.
 */

const VARIANT = "wizard" as const;

import { capture } from "./capture";

export const AlertsTracking = {
  // ── Page-level ──

  /** Alerts list page mounted. */
  pageViewed: (pageSource: string) => {
    capture("alerts.page_viewed", { variant: VARIANT, page_source: pageSource });
  },

  /** Rules list fetched successfully from API. */
  rulesLoaded: (count: number) => {
    capture("alerts.rules_loaded", { variant: VARIANT, count });
  },

  // ── Creation flow ──

  /** User clicked "Create" button on list page. */
  createClicked: () => {
    capture("alerts.create_clicked", { variant: VARIANT });
  },

  /** Create form / wizard loaded and rendered. */
  createFormLoaded: () => {
    capture("alerts.create_form_loaded", { variant: VARIANT, form_type: "wizard" });
  },

  /** User selected a rule type (alert vs report). */
  ruleTypeSelected: (ruleType: string) => {
    capture("alerts.rule_type_selected", { variant: VARIANT, rule_type: ruleType });
  },

  /** User selected an entity type. */
  entityTypeSelected: (entityType: string) => {
    capture("alerts.entity_type_selected", { variant: VARIANT, entity_type: entityType });
  },

  /** User selected a metric. */
  metricSelected: (metricName: string, category: string) => {
    capture("alerts.metric_selected", {
      variant: VARIANT,
      metric_name: metricName,
      category,
    });
  },

  /** User configured threshold (filled in threshold value). */
  thresholdConfigured: (thresholdType: string, comparisonType: string) => {
    capture("alerts.threshold_configured", {
      variant: VARIANT,
      threshold_type: thresholdType,
      comparison_type: comparisonType,
    });
  },

  /** User configured schedule (frequency/cron). */
  scheduleConfigured: (frequency: string) => {
    capture("alerts.schedule_configured", { variant: VARIANT, frequency });
  },

  /** User configured notification channels. */
  notifierConfigured: (channels: string[]) => {
    capture("alerts.notifier_configured", {
      variant: VARIANT,
      channels,
      channel_count: channels.length,
    });
  },

  /** User clicked preview. */
  previewRequested: (metricName: string) => {
    capture("alerts.preview_requested", { variant: VARIANT, metric_name: metricName });
  },

  /** Preview loaded successfully. */
  previewLoaded: (metricName: string) => {
    capture("alerts.preview_loaded", { variant: VARIANT, metric_name: metricName });
  },

  /** Preview failed. */
  previewFailed: (error: string) => {
    capture("alerts.preview_failed", { variant: VARIANT, error });
  },

  /** User clicked submit / save. */
  createSubmitted: (ruleType: string) => {
    capture("alerts.create_submitted", { variant: VARIANT, rule_type: ruleType });
  },

  /** Rule created successfully via API. */
  ruleCreated: (ruleType: string) => {
    capture("alerts.rule_created", { variant: VARIANT, rule_type: ruleType, source: "wizard" });
  },

  /** Rule updated successfully via API. */
  ruleUpdated: (ruleId: string) => {
    capture("alerts.rule_edited", { variant: VARIANT, rule_id: ruleId });
  },

  /** Create/update failed (API error). */
  saveFailed: (error: string) => {
    capture("alerts.create_failed", { variant: VARIANT, error });
  },

  /** Wizard step changed. */
  wizardStepChanged: (fromStep: number, toStep: number) => {
    capture("alerts.wizard_step_changed", {
      variant: VARIANT,
      from_step: fromStep,
      to_step: toStep,
    });
  },

  /** Template selected from template picker. */
  templateSelected: (templateId: string, templateName: string) => {
    capture("alerts.template_selected", {
      variant: VARIANT,
      template_id: templateId,
      template_name: templateName,
    });
  },

  // ── CRUD (post-creation) ──

  /** Rule deleted. */
  ruleDeleted: (ruleId: string) => {
    capture("alerts.rule_deleted", { variant: VARIANT, rule_id: ruleId });
  },

  /** Rule toggled active/inactive. */
  ruleToggled: (ruleId: string, newStatus: string) => {
    capture("alerts.rule_toggled", { variant: VARIANT, rule_id: ruleId, new_status: newStatus });
  },

  /** Rule cloned successfully. */
  ruleCloned: (ruleId: string) => {
    capture("alerts.rule_cloned", { variant: VARIANT, rule_id: ruleId });
  },

  /** Rule detail page viewed. */
  ruleDetailViewed: (ruleId: string) => {
    capture("alerts.rule_detail_viewed", { variant: VARIANT, rule_id: ruleId });
  },

  // ── Filters & search ──

  /** User applied a filter on the list page. */
  filterApplied: (filterType: string, filterValue: string) => {
    capture("alerts.filter_applied", {
      variant: VARIANT,
      filter_type: filterType,
      filter_value: filterValue,
    });
  },

  /** User searched on the list page. */
  searchUsed: (query: string) => {
    capture("alerts.search_used", { variant: VARIANT, query_length: query.length });
  },
};
